import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import banner from '../assets/tpp-claims-banner.jpg';
import testDamageEvidencePng from '../assets/test-damage-evidence.png';
import Layout from '../components/Layout';
import ProgressSteps from '../components/ProgressSteps';
import { RootState } from '../store/store';
import InventoryEmptyOverlayDialog from '../components/InventoryEmptyOverlayDialog';
import { createInventoryItem, getInventoryItems, uploadInventoryAttachments } from '../services/api';
import { useClaimTokenResume } from '../hooks/useClaimTokenResume';
import { buildClaimResumePath } from '../utils/claimTokenResume';
import { getErrorMessage } from '../utils/errorHandler';
import {
  acknowledgeInventoryEmptyOverlay,
  hasAcknowledgedInventoryEmptyOverlay,
} from '../utils/inventoryEmptyOverlay';
import { config } from '../config';
import {
  getInventoryEvidenceFileLimitError,
  MAX_FILES_PER_INVENTORY_ITEM,
  MAX_FILES_PER_INVENTORY_ITEM_ERROR,
} from '../utils/inventoryFileLimits';

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 80 }, (_, i) => String(currentYear - i));
const months = [
  { label: 'Jan', value: '01' },
  { label: 'Feb', value: '02' },
  { label: 'Mar', value: '03' },
  { label: 'Apr', value: '04' },
  { label: 'May', value: '05' },
  { label: 'Jun', value: '06' },
  { label: 'Jul', value: '07' },
  { label: 'Aug', value: '08' },
  { label: 'Sep', value: '09' },
  { label: 'Oct', value: '10' },
  { label: 'Nov', value: '11' },
  { label: 'Dec', value: '12' },
];

const normalizeDecimalInput = (raw: string): string => {
  // Allow digits + optional dot and commas; strip commas and $ for backend.
  return String(raw || '').replace(/,/g, '').replace(/\$/g, '').trim();
};

const AddInventoryItem: React.FC = () => {
  const navigate = useNavigate();
  const claimData = useSelector((state: RootState) => state.claim);
  const { claimToken, fromClaimStatus, resumeError, isClaimReady, isIncomplete } = useClaimTokenResume();
  const incompleteFromStatus = fromClaimStatus && isIncomplete;

  const claimId = claimData.claimId;

  const [isSavingItem, setIsSavingItem] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [inventoryEmptyOverlayOpen, setInventoryEmptyOverlayOpen] = useState(false);

  const [itemDescription, setItemDescription] = useState('');
  const [damageDescription, setDamageDescription] = useState('');
  const [purchaseMonth, setPurchaseMonth] = useState('');
  const [purchaseYear, setPurchaseYear] = useState('');
  const [price, setPrice] = useState('');
  const [hasReceipt, setHasReceipt] = useState('');
  const [hasDamageEvidence, setHasDamageEvidence] = useState('');
  const [receiptFiles, setReceiptFiles] = useState<FileList | null>(null);
  const [evidenceFiles, setEvidenceFiles] = useState<FileList | null>(null);

  useEffect(() => {
    if (!claimId) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const items = await getInventoryItems(claimId);
        if (cancelled) return;
        const count = Array.isArray(items) ? items.length : 0;
        if (count === 0 && !hasAcknowledgedInventoryEmptyOverlay(claimId)) {
          setInventoryEmptyOverlayOpen(true);
        }
      } catch {
        // If count cannot be loaded, do not block the form with the overlay.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [claimId]);

  const navigateAfterSave = () => {
    navigate(buildClaimResumePath('/damages', claimToken, fromClaimStatus));
  };

  const handleInventoryEmptyOverlayOk = () => {
    if (claimId != null) {
      acknowledgeInventoryEmptyOverlay(claimId);
    }
    setInventoryEmptyOverlayOpen(false);
  };

  const monthYearValue = useMemo(() => {
    if (!purchaseMonth || !purchaseYear) return null;
    return `${purchaseYear}-${purchaseMonth}`;
  }, [purchaseMonth, purchaseYear]);

  const validateEntry = (): string | null => {
    if (!itemDescription.trim()) return 'Item Description (make/model) is required.';
    if (!damageDescription.trim()) return 'Description of damage is required.';
    if ((purchaseMonth && !purchaseYear) || (!purchaseMonth && purchaseYear)) {
      return 'Please select both month and year for the purchase date.';
    }
    if (!price.trim()) return 'Price is required.';

    const normalizedPrice = normalizeDecimalInput(price);
    if (!/^\d+(\.\d{1,2})?$/.test(normalizedPrice)) {
      return 'Enter a valid price (up to 2 decimal places).';
    }

    if (hasReceipt === '') return 'Please answer whether you have the receipt.';
    if (hasDamageEvidence === '') return 'Please answer whether you have evidence of the damage.';

    if (hasReceipt === 'yes' && (!receiptFiles || receiptFiles.length === 0)) {
      return 'Receipt file is required when receipt answer is Yes.';
    }
    if (hasDamageEvidence === 'yes' && (!evidenceFiles || evidenceFiles.length === 0)) {
      return 'Damage evidence file is required when evidence answer is Yes.';
    }
    const fileLimitError = getInventoryEvidenceFileLimitError(hasDamageEvidence, evidenceFiles);
    if (fileLimitError) {
      return fileLimitError;
    }

    return null;
  };

  const handleEvidenceFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) {
      setEvidenceFiles(null);
      if (errorMessage === MAX_FILES_PER_INVENTORY_ITEM_ERROR) {
        setErrorMessage('');
      }
      return;
    }
    if (selected.length >= MAX_FILES_PER_INVENTORY_ITEM + 1) {
      e.target.value = '';
      setEvidenceFiles(null);
      setErrorMessage(MAX_FILES_PER_INVENTORY_ITEM_ERROR);
      return;
    }
    setEvidenceFiles(selected);
    if (errorMessage === MAX_FILES_PER_INVENTORY_ITEM_ERROR) {
      setErrorMessage('');
    }
  };

  const handleLoadTestData = async () => {
    setErrorMessage('');
    setItemDescription(
      'Samsung 32-inch H5000F Smart TV. High-definition (1080p). Features streaming services, live channels, and a Gaming Hub.'
    );
    setDamageDescription('Screen panel cracked with visible vertical lines and intermittent flicker after water exposure.');
    setPrice('100');
    setPurchaseMonth('01');
    setPurchaseYear('2022');
    setHasReceipt('no');
    setHasDamageEvidence('yes');
    setReceiptFiles(null);
    try {
      const res = await fetch(testDamageEvidencePng);
      if (!res.ok) throw new Error(`Failed to load test evidence: ${res.status}`);
      const buf = await res.arrayBuffer();
      const dt = new DataTransfer();
      dt.items.add(new File([buf], 'test-damage-evidence.png', { type: 'image/png' }));
      setEvidenceFiles(dt.files);
    } catch {
      setErrorMessage('Could not load the test damage evidence image.');
    }
  };

  const handleSubmit = async () => {
    if (!claimId) return;

    const fileLimitError = getInventoryEvidenceFileLimitError(hasDamageEvidence, evidenceFiles);
    if (fileLimitError) {
      setErrorMessage(fileLimitError);
      return;
    }

    const validationError = validateEntry();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    const evidenceToUpload =
      hasDamageEvidence === 'yes' && evidenceFiles ? Array.from(evidenceFiles) : [];
    if (evidenceToUpload.length >= MAX_FILES_PER_INVENTORY_ITEM + 1) {
      setErrorMessage(MAX_FILES_PER_INVENTORY_ITEM_ERROR);
      return;
    }

    setErrorMessage('');
    setIsSavingItem(true);
    try {
      const payload = {
        item_description: itemDescription.trim(),
        damage_description: damageDescription.trim(),
        date_of_purchase: monthYearValue,
        price: normalizeDecimalInput(price),
        has_receipt: hasReceipt === 'yes',
        has_damage_evidence: hasDamageEvidence === 'yes',
      };

      const created = await createInventoryItem(claimId, payload);

      if (hasReceipt === 'yes' && receiptFiles) {
        await uploadInventoryAttachments(claimId, created.id, Array.from(receiptFiles), 'inventory_receipt');
      }
      if (evidenceToUpload.length > 0) {
        await uploadInventoryAttachments(claimId, created.id, evidenceToUpload, 'inventory_evidence');
      }

      navigateAfterSave();
    } catch (error) {
      setErrorMessage(getErrorMessage(error) || 'Failed to add inventory item.');
    } finally {
      setIsSavingItem(false);
    }
  };

  if (!isClaimReady) {
    if (resumeError) {
      return (
        <Layout>
          <Box sx={{ maxWidth: 560, mx: 'auto', py: 6, px: 2 }}>
            <Alert severity="error">{resumeError}</Alert>
          </Box>
        </Layout>
      );
    }

    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="banner">
        <img src={banner} alt="TPP Claims Banner" />
        <div className="banner-content">
          <h1>File a Claim</h1>
          <h2>Tenant Protection Plan</h2>
          <p>Follow our easy-to-use claim process to submit your claim!</p>
        </div>
      </div>

      <ProgressSteps currentStep="damages" hide={incompleteFromStatus} />

      <Box
        sx={{
          width: '100%',
          maxWidth: { md: 880, lg: 960 },
          mx: { md: 'auto' },
          px: { md: 0 },
          // Match InventoryList / Incident: space below the primary actions.
          pb: 3,
        }}
      >
      <Typography variant="h4" gutterBottom>
        Damage Inventory of Items
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {incompleteFromStatus
          ? 'Your claim is incomplete. Add (or update) items one at a time. You can re-submit when you are done.'
          : 'Add items one at a time. You can submit the claim after you add all inventory items.'}
      </Typography>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          label="Item make and model"
          multiline
          minRows={3}
          required
          value={itemDescription}
          onChange={(e) => setItemDescription(e.target.value)}
          placeholder="Example: Sony V35 40in TV"
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          fullWidth
          label="Description of damage"
          multiline
          minRows={3}
          required
          value={damageDescription}
          onChange={(e) => setDamageDescription(e.target.value)}
          placeholder="Describe what damage occurred to this item"
          InputLabelProps={{ shrink: true }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" component="div">
            <strong>Purchase Price:</strong>
          </Typography>
          <TextField
            required
            size="small"
            value={price}
            onChange={(e) => setPrice(e.target.value.replace(/\$/g, ''))}
            placeholder="0.00"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{
              inputMode: 'decimal',
              pattern: '[0-9]*\\.?[0-9]{0,2}',
              step: '0.01',
              min: '0',
              'aria-label': 'Purchase price in dollars',
            }}
            sx={{ width: { xs: '100%', sm: 280 } }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" component="div">
            <strong>Purchase Date:</strong>
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              alignItems: 'flex-start',
            }}
          >
          <FormControl size="small" sx={{ width: { xs: '100%', sm: 170 }, flex: '0 0 auto' }}>
            <InputLabel id="purchase-month-label">Month</InputLabel>
            <Select
              labelId="purchase-month-label"
              value={purchaseMonth}
              label="Month"
              onChange={(e) => setPurchaseMonth(String(e.target.value))}
            >
              <MenuItem value="">
                <em>Select month</em>
              </MenuItem>
              {months.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ width: { xs: '100%', sm: 145 }, flex: '0 0 auto' }}>
            <InputLabel id="purchase-year-label">Year</InputLabel>
            <Select
              labelId="purchase-year-label"
              value={purchaseYear}
              label="Year"
              onChange={(e) => setPurchaseYear(String(e.target.value))}
            >
              <MenuItem value="">
                <em>Select year</em>
              </MenuItem>
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          </Box>
        </Box>

        <FormControl>
          <Typography>
            <strong>Do you have the receipt?</strong>
          </Typography>
          <RadioGroup row value={hasReceipt} onChange={(e) => setHasReceipt(e.target.value)}>
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>

        {hasReceipt === 'yes' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button component="label" variant="outlined" sx={{ width: 'fit-content' }}>
              Choose Receipt File(s)
              <input
                hidden
                type="file"
                multiple
                accept="image/*,application/pdf,.doc,.docx"
                onChange={(e) => setReceiptFiles(e.target.files)}
              />
            </Button>
            {receiptFiles && receiptFiles.length > 0 && (
              <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                {Array.from(receiptFiles)
                  .map((f) => f.name)
                  .join(', ')}
              </Typography>
            )}
          </Box>
        )}

        <FormControl>
          <Typography>
            <strong>Do you have evidence of the damage?</strong>
          </Typography>
          <RadioGroup row value={hasDamageEvidence} onChange={(e) => setHasDamageEvidence(e.target.value)}>
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>

        {hasDamageEvidence === 'yes' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button component="label" variant="outlined" sx={{ width: 'fit-content' }}>
              Choose Evidence File(s)
              <input
                hidden
                type="file"
                multiple
                accept="image/*,video/*,application/pdf,.doc,.docx"
                onChange={handleEvidenceFilesChange}
              />
            </Button>
            {evidenceFiles && evidenceFiles.length > 0 && (
              <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                {Array.from(evidenceFiles)
                  .map((f) => f.name)
                  .join(', ')}
              </Typography>
            )}
          </Box>
        )}

        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Upload any images, videos, files related to damages. You may select up to 2 evidence files per item. Max file
          size: 40MB. Trim videos to less than 10 seconds.
        </Typography>

        {config.APP_MODE !== 'production' && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Link
              component="button"
              type="button"
              underline="always"
              variant="body1"
              onClick={handleLoadTestData}
              sx={{
                color: 'text.secondary',
                cursor: 'pointer',
              }}
            >
              Load test data
            </Link>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(buildClaimResumePath('/damages', claimToken, fromClaimStatus))}
            fullWidth
            disabled={isSavingItem}
            sx={{
              color: 'var(--primary-color)',
              borderColor: 'var(--primary-color)',
              fontWeight: 600,
              '&:hover': { backgroundColor: 'rgba(var(--primary-color-rgb), 0.04)' },
            }}
          >
            Back to List
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={isSavingItem}
            sx={{ backgroundColor: 'var(--primary-color)', fontWeight: 700 }}
          >
            {isSavingItem ? <CircularProgress size={22} /> : 'Add Item'}
          </Button>
        </Box>
      </Box>
      </Box>

      <InventoryEmptyOverlayDialog
        open={inventoryEmptyOverlayOpen}
        onOk={handleInventoryEmptyOverlayOk}
      />
    </Layout>
  );
};

export default AddInventoryItem;
